import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash, MinusCircle, PlusCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Reusable dynamic compensation line-item editor.
 *
 * Props:
 *  - model / setModel:       "monthly" | "daily" | "hourly" | "session_based" | ...
 *  - lines / setLines:       [{ label, amount, kind }]
 *  - showTemplates:          when true, renders a "Select template" dropdown
 *                            above the table which populates the lines with
 *                            an in-place confirmation prompt.
 *  - disabled:               hide/lock the entire section (used when the
 *                            employee is a parent/guardian).
 *
 * Notes:
 *  - "Compensation Type" is a free-text field per line — Basic Pay, HRA,
 *    Travel Allowance, PF, ESI, Bonus, or anything custom.
 *  - Each line carries an Earning ↔ Deduction toggle. The net salary
 *    readout at the bottom auto-updates as the user types.
 */
const MODELS = [
  { value: "monthly",       label: "Monthly" },
  { value: "daily",         label: "Daily" },
  { value: "hourly",        label: "Hourly" },
  { value: "session_based", label: "Session-based" },
  { value: "part_time",     label: "Part-time / Contract" },
  { value: "visiting",      label: "Visiting" },
  { value: "custom",        label: "Custom" },
];

export default function CompensationLineItems({
  model, setModel, lines, setLines,
  showTemplates = true, disabled = false, dataTestidPrefix = "comp",
}) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  useEffect(() => {
    if (!showTemplates || disabled) return;
    (async () => {
      try {
        const { data } = await api.get("/compensation-templates");
        setTemplates(data || []);
      } catch {
        // Non-fatal — dropdown just stays empty.
      }
    })();
  }, [showTemplates, disabled]);

  const applyTemplate = (tplId) => {
    const tpl = templates.find(t => t.id === tplId);
    if (!tpl) return;
    if ((lines || []).length > 0) {
      if (!window.confirm(
        `Replace ${lines.length} existing line item${lines.length > 1 ? "s" : ""} with template "${tpl.name}"?`,
      )) {
        setSelectedTemplate("");
        return;
      }
    }
    setModel(tpl.model || "monthly");
    setLines((tpl.lines || []).map(l => ({
      label: l.label, amount: Number(l.amount) || 0, kind: l.kind || "earning",
    })));
    setSelectedTemplate(tplId);
    toast.success(`Template "${tpl.name}" applied — you can now customise the lines`);
  };

  const add = () => setLines([
    ...(lines || []),
    { label: "", amount: 0, kind: "earning",
      _ui_key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `k-${Date.now()}-${Math.random()}` },
  ]);
  const patch = (idx, p) => setLines(lines.map((l, i) => (i === idx ? { ...l, ...p } : l)));
  const remove = (idx) => setLines(lines.filter((_, i) => i !== idx));

  const netSalary = (lines || []).reduce((acc, l) => {
    const amt = Number(l.amount) || 0;
    return l.kind === "deduction" ? acc - amt : acc + amt;
  }, 0);

  if (disabled) {
    return (
      <div className="rounded border border-dashed p-4 text-center text-xs text-muted-foreground" data-testid={`${dataTestidPrefix}-disabled`}>
        Compensation does not apply to Parent/Guardian accounts.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={`${dataTestidPrefix}-section`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Compensation Model</Label>
          <Select value={model || "monthly"} onValueChange={setModel}>
            <SelectTrigger data-testid={`${dataTestidPrefix}-model`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {MODELS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {showTemplates && (
          <div>
            <Label>Select Template (optional)</Label>
            <Select value={selectedTemplate} onValueChange={applyTemplate}>
              <SelectTrigger data-testid={`${dataTestidPrefix}-template`}>
                <SelectValue placeholder={templates.length ? "Pick a template…" : "No templates yet"} />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} ({t.lines?.length || 0} line{(t.lines?.length || 0) === 1 ? "" : "s"})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="border rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">Compensation Type</th>
              <th className="text-right px-2 py-2 w-32">Amount (₹)</th>
              <th className="text-center px-2 py-2 w-32">Kind</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {(lines || []).length === 0 ? (
              <tr><td colSpan={4} className="text-center px-3 py-6 text-muted-foreground text-xs" data-testid={`${dataTestidPrefix}-empty`}>
                No line items yet — click <b>Add row</b> to start.
              </td></tr>
            ) : (
              (lines || []).map((line, i) => (
                <tr key={line._ui_key || line.id || `row-${i}`} data-testid={`${dataTestidPrefix}-row-${i}`}>
                  <td className="px-2 py-1">
                    <Input
                      value={line.label} onChange={e => patch(i, { label: e.target.value })}
                      placeholder="e.g. Basic Pay, HRA, PF"
                      className="h-9"
                      data-testid={`${dataTestidPrefix}-label-${i}`}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <Input
                      type="number" value={line.amount}
                      onChange={e => patch(i, { amount: parseFloat(e.target.value) || 0 })}
                      className="h-9 text-right"
                      data-testid={`${dataTestidPrefix}-amount-${i}`}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <button
                      type="button"
                      onClick={() => patch(i, { kind: line.kind === "deduction" ? "earning" : "deduction" })}
                      className={`w-full h-9 rounded border text-xs font-medium inline-flex items-center justify-center gap-1 ${
                        line.kind === "deduction"
                          ? "bg-rose-50 border-rose-200 text-rose-700"
                          : "bg-emerald-50 border-emerald-200 text-emerald-700"
                      }`}
                      data-testid={`${dataTestidPrefix}-kind-${i}`}
                    >
                      {line.kind === "deduction"
                        ? <><MinusCircle size={14} /> Deduction</>
                        : <><PlusCircle  size={14} /> Earning</>}
                    </button>
                  </td>
                  <td className="px-2 py-1 text-center">
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => remove(i)} data-testid={`${dataTestidPrefix}-delete-${i}`}>
                      <Trash size={14} />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr className="bg-muted/30">
              <td className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Net salary</td>
              <td className="px-2 py-2 text-right font-semibold" data-testid={`${dataTestidPrefix}-net`}>
                ₹{netSalary.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>

      <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`${dataTestidPrefix}-add-row`}>
        <Plus size={14} className="mr-1" /> Add row
      </Button>
    </div>
  );
}
